// src/components/Layout.js

import React, { useContext, useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext";
import TrialBanner from "./TrialBanner";
import {
  HiOutlineHome, HiOutlineUsers, HiOutlineUserGroup, HiOutlineReceiptPercent,
  HiOutlineShieldCheck, HiOutlineKey, HiOutlineTag, HiOutlineCube, HiOutlineClock,
  HiOutlineArchiveBox, HiOutlineBuildingStorefront, HiOutlineUserCircle, HiOutlineCog6Tooth,
  HiOutlineBuildingOffice2, HiOutlineArrowLeftOnRectangle, HiOutlineXMark,
  HiOutlineChevronRight, HiOutlineBars3, HiOutlineBell, HiOutlineDocumentChartBar,
  HiOutlineChevronDoubleLeft, HiOutlineChevronDoubleRight,
  HiOutlineArchiveBoxArrowDown,
  HiOutlineClipboardDocumentCheck,
  HiOutlineGiftTop,
  HiOutlineSquares2X2
} from "react-icons/hi2";

// Updated NavItem to handle collapsed state and show tooltips
const NavItem = ({ to, icon, children, onClick, isCollapsed }) => (
  <li className="relative group">
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center w-full px-4 py-2.5 rounded-xl transition-all duration-200 text-sm font-medium ${
          isActive
            ? "bg-svinno-600 text-white shadow-lg shadow-svinno-900/20"
            : "text-slate-400 hover:bg-slate-800 hover:text-white"
        } ${isCollapsed ? 'justify-center' : ''}`
      }
    >
      {React.cloneElement(icon, { className: "w-5 h-5 flex-shrink-0" })}
      <span className={`ml-3 transition-opacity duration-200 ${isCollapsed ? 'md:opacity-0 md:hidden' : 'opacity-100'}`}>{children}</span>
    </NavLink>
    {isCollapsed && (
      <div className="absolute left-full top-1/2 -translate-y-1/2 ml-4 px-2 py-1 bg-slate-900 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50 shadow-md border border-slate-700">
        {children}
      </div>
    )}
  </li>
);

// Updated CollapsibleMenu to handle collapsed state
const CollapsibleMenu = ({ title, icon, children, isOpen, setIsOpen, isCollapsed }) => {
  const handleToggle = () => {
    if (!isCollapsed) {
      setIsOpen((prev) => !prev);
    }
  };

  return (
    <li>
      <button
        type="button"
        className={`flex items-center w-full px-4 py-2.5 rounded-xl text-slate-400 hover:bg-slate-800 hover:text-white transition-colors duration-200 focus:outline-none ${isCollapsed ? 'justify-center' : ''}`}
        onClick={handleToggle}
      >
        {React.cloneElement(icon, { className: "w-5 h-5 flex-shrink-0" })}
        {!isCollapsed && (
          <>
            <span className="ml-3 text-sm font-medium">{title}</span>
            <HiOutlineChevronRight className={`w-4 h-4 ml-auto transition-transform ${isOpen ? "rotate-90" : ""}`} />
          </>
        )}
      </button>
      {!isCollapsed && isOpen && (
        <ul className="pl-4 mt-1 space-y-1 ml-4 border-l border-slate-700">
          {children}
        </ul>
      )}
    </li>
  );
};

// Profile Dropdown component for the header
const ProfileDropdown = ({ user, onLogout }) => {
  if (!user) return null;

  return (
    <div className="relative group">
      <button className="flex items-center space-x-2 focus:outline-none">
        <img
          src={user.profilePic || `https://ui-avatars.com/api/?name=${user.username}&background=0284c7&color=fff&size=96`}
          alt="Profile"
          className="w-9 h-9 rounded-lg ring-2 ring-transparent group-hover:ring-svinno-500 transition shadow-sm"
        />
      </button>

      <div className="absolute right-0 mt-2 w-56 origin-top-right bg-white rounded-xl shadow-xl ring-1 ring-black ring-opacity-5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 border border-slate-100">
        <div className="py-1">
          <div className="px-4 py-3 border-b border-slate-100">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Signed in as</p>
            <p className="text-sm font-bold text-slate-800 truncate">{user.username}</p>
          </div>
          <NavLink to="/profile" className="flex items-center w-full px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-svinno-600">
            <HiOutlineUserCircle className="w-5 h-5 mr-2 opacity-70" />
            Your Profile
          </NavLink>
          <NavLink to="/settings" className="flex items-center w-full px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-svinno-600">
            <HiOutlineCog6Tooth className="w-5 h-5 mr-2 opacity-70" />
            Settings
          </NavLink>
          <div className="border-t border-slate-100 my-1"></div>
          <button onClick={onLogout} className="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50">
            <HiOutlineArrowLeftOnRectangle className="w-5 h-5 mr-2" />
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

const Layout = ({ children, title, headerActions, actionButton }) => {
  const { logout, user, hasPermission } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    JSON.parse(localStorage.getItem('sidebar-collapsed')) || false
  );
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', JSON.stringify(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const closeSidebar = () => setIsSidebarOpen(false);

  // Grouped navigation definition for clearer layout
  const navSections = [
    {
      id: 'overview',
      title: 'Overview',
      items: [
        { to: '/dashboard', icon: <HiOutlineHome />, label: 'Dashboard', perm: null }
      ]
    },
    {
      id: 'people',
      title: 'People',
      items: [
        { to: '/users', icon: <HiOutlineUsers />, label: 'Users', perm: 'manage_users' },
        { to: '/customers', icon: <HiOutlineUserGroup />, label: 'Customers', perm: 'manage_customers' }
      ]
    },
    {
      id: 'sales',
      title: 'Sales & Orders',
      items: [
        { to: '/transactions', icon: <HiOutlineReceiptPercent />, label: 'Transactions', perm: 'manage_transactions' },
        { to: '/purchase-orders', icon: <HiOutlineClipboardDocumentCheck />, label: 'Purchase Orders', perm: 'manage_products' },
        { to: '/promotions', icon: <HiOutlineGiftTop />, label: 'Promotions', perm: 'manage_products' }
      ]
    },
    {
      id: 'inventory',
      title: 'Inventory',
      items: [
        { to: '/products', icon: <HiOutlineCube />, label: 'Products', perm: 'manage_products' },
        { to: '/batches', icon: <HiOutlineArchiveBoxArrowDown />, label: 'Batches', perm: 'manage_products' },
        { to: '/inventory-transfers', icon: <HiOutlineArchiveBox />, label: 'Inventory Transfers', perm: 'manage_inventory' },
        { to: '/stock-history', icon: <HiOutlineClock />, label: 'Inventory Log', perm: 'manage_inventory' },
        { to: '/inventory/by-store', icon: <HiOutlineDocumentChartBar />, label: 'Store Report', perm: 'manage_inventory' },
        { to: '/inventory/report', icon: <HiOutlineSquares2X2 />, label: 'Inventory Report', perm: 'manage_inventory' }
      ]
    },
    {
      id: 'settings',
      title: 'Admin',
      items: [
        { to: '/roles', icon: <HiOutlineShieldCheck />, label: 'Roles', perm: 'manage_roles' },
        { to: '/permissions', icon: <HiOutlineKey />, label: 'Permissions', perm: 'manage_permissions' },
        { to: '/categories', icon: <HiOutlineTag />, label: 'Categories', perm: 'manage_categories' },
        { to: '/stores', icon: <HiOutlineBuildingStorefront />, label: 'Stores', perm: 'manage_settings' },
        { to: '/warehouses', icon: <HiOutlineArchiveBox />, label: 'Warehouses', perm: 'manage_warehouses' },
        // { to: '/settings', icon: <HiOutlineCog6Tooth />, label: 'Settings', perm: 'manage_settings' }
      ]
    }
  ];

  const role = user?.role;
  const isTrial = user?.subscription?.isTrial;
  const trialEndsAt = user?.subscription?.trialEndsAt;
  const showTrialBanner = isTrial && (role === "organization_admin" || role === "organization_manager");

  const filteredNavItems = navSections.flatMap(section => {
    const hasVisibleItems = section.items.some(it => 
      (!it.perm || hasPermission(it.perm)) && 
      it.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return hasVisibleItems || searchTerm === "" ? [
      { title: section.title, isTitle: true }, // Add category title
      ...section.items.filter(it => 
        (!it.perm || hasPermission(it.perm)) && 
        it.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    ] : [];
  });

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans">
      {isSidebarOpen && <div onClick={closeSidebar} className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-30 md:hidden"></div>}

      <aside className={`fixed inset-y-0 left-0 bg-slate-900 border-r border-slate-800 text-white flex flex-col p-4 shadow-xl z-40 transform transition-all duration-300 ease-in-out ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} md:relative md:translate-x-0 ${isSidebarCollapsed ? 'md:w-20' : 'md:w-64'}`}>
        
        {/* Brand Header */}
        <div className={`flex items-center justify-between mb-8 px-1 ${isSidebarCollapsed ? 'justify-center' : ''}`}>
          {!isSidebarCollapsed ? (
             <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-svinno-600 rounded-lg flex items-center justify-center text-white font-bold text-lg shadow-lg">S</div>
                <span className="text-xl font-bold text-white tracking-tight font-display">Svinno Retail</span>
             </div>
          ) : (
             <div className="w-8 h-8 bg-svinno-600 rounded-lg flex items-center justify-center text-white font-bold text-lg shadow-lg">S</div>
          )}
          <button onClick={closeSidebar} className="p-1 rounded-full hover:bg-slate-800 text-slate-400 hover:text-white md:hidden">
            <HiOutlineXMark className="w-6 h-6" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto -mr-2 pr-2 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
          {/* Quick Search */}
          {!isSidebarCollapsed && (
            <div className="px-1 mb-6">
              <input
                placeholder="Quick jump..."
                className="w-full text-sm py-2 px-3 rounded-lg bg-slate-800 text-slate-200 placeholder-slate-500 border border-slate-700 focus:outline-none focus:ring-1 focus:ring-svinno-500 focus:border-svinno-500 transition-all"
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          )}

          <ul className="space-y-1">
            {filteredNavItems.map((it, idx) => (
              it.isTitle ? (
                !isSidebarCollapsed && (
                  <li key={`${it.title}-${idx}`} className="px-4 mt-6 mb-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    {it.title}
                  </li>
                )
              ) : (
                <NavItem key={it.to} to={it.to} icon={it.icon} onClick={closeSidebar} isCollapsed={isSidebarCollapsed}>
                  {it.label}
                </NavItem>
              )
            ))}
            
            <li className="my-4 border-t border-slate-800"></li>
            
            <NavItem to="/profile" icon={<HiOutlineUserCircle />} onClick={closeSidebar} isCollapsed={isSidebarCollapsed}>Profile</NavItem>
            {hasPermission("manage_settings") && <NavItem to="/settings" icon={<HiOutlineCog6Tooth />} onClick={closeSidebar} isCollapsed={isSidebarCollapsed}>Settings</NavItem>}
          </ul>
        </nav>

        <div className="mt-auto pt-4 border-t border-slate-800">
          <button onClick={() => setIsSidebarCollapsed(prev => !prev)} className="hidden md:flex items-center justify-center w-full h-9 mb-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            {isSidebarCollapsed ? <HiOutlineChevronDoubleRight className="w-5 h-5" /> : <HiOutlineChevronDoubleLeft className="w-5 h-5" />}
          </button>
          
          {user && !isSidebarCollapsed && (
            <div className="p-3 bg-slate-800 rounded-xl mb-3 border border-slate-700 flex items-center gap-3">
               <div className="w-8 h-8 rounded-full bg-svinno-600 flex items-center justify-center text-xs font-bold text-white uppercase shrink-0">
                  {user.username.substring(0,2)}
               </div>
               <div className="min-w-0">
                  <p className="font-semibold text-white text-sm truncate">{user.username}</p>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wide truncate">{user.role?.replace("_", " ")}</p>
               </div>
            </div>
          )}
          
          <button onClick={handleLogout} className={`flex items-center justify-center w-full px-4 py-2.5 bg-red-500/10 text-red-400 rounded-xl hover:bg-red-500 hover:text-white transition-all duration-200 font-medium group border border-transparent hover:border-red-600`}>
            <HiOutlineArrowLeftOnRectangle className="w-5 h-5 group-hover:scale-110 transition-transform" />
            {!isSidebarCollapsed && <span className="ml-2">Logout</span>}
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden relative">
        <header className="w-full bg-white border-b border-slate-200 shadow-sm z-20 h-16">
          {showTrialBanner && <TrialBanner trialEndsAt={trialEndsAt} />}
          <div className="flex items-center justify-between px-4 h-full md:px-8">
            <button onClick={() => setIsSidebarOpen(true)} className="p-2 -ml-2 rounded-lg text-slate-500 hover:bg-slate-100 focus:outline-none md:hidden transition-colors">
              <HiOutlineBars3 className="w-6 h-6" />
            </button>
            
            <div className="flex-1"></div>
            
            <div className="flex items-center gap-4">
              {headerActions}
              {actionButton}
              
              <div className="h-8 w-px bg-slate-200 mx-2"></div>

              <button className="relative p-2 text-slate-400 hover:text-svinno-600 hover:bg-slate-50 rounded-full focus:outline-none transition-all">
                <HiOutlineBell className="w-6 h-6" />
                <span className="absolute top-1.5 right-1.5 block h-2 w-2 rounded-full bg-red-500 ring-2 ring-white"></span>
              </button>
              
              <ProfileDropdown user={user} onLogout={handleLogout} />
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-8 overflow-y-auto scroll-smooth bg-slate-50">
          <div className="max-w-7xl mx-auto">
             {title && (
               <div className="mb-8">
                 <h1 className="text-3xl font-bold text-slate-900 tracking-tight font-display">{title}</h1>
               </div>
             )}
             {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
